import { useEffect } from 'react';
import { animate, stagger, onScroll } from 'animejs';
import type { ScrollObserver } from 'animejs';

/**
 * Protocol-Motion (anime.js v4) — nahtlose, dezente Übergänge im zkPass-Stil.
 *
 * Gesteuert über data-Attribute im Markup:
 *   data-hero-seq            → Hero-Entrance-Sequenz (einmalig beim Laden, gestaffelt)
 *   data-motion="rise"       → Element steigt beim Eintritt in den Viewport sanft auf
 *   data-motion="slide-left" → Element wischt scroll-synchron von links herein
 *   data-motion="slide-right"→ Element wischt scroll-synchron von rechts herein
 *
 * Respektiert prefers-reduced-motion (dann: alles sofort sichtbar, keine Animation).
 */
export function useProtocolMotion() {
  useEffect(() => {
    const noMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (noMotion) return;

    const observers: ScrollObserver[] = [];

    // ── 1) Hero-Entrance: Kicker → Headline → Sub → CTAs → Stats ──────────
    const heroEls = document.querySelectorAll<HTMLElement>('[data-hero-seq]');
    if (heroEls.length) {
      animate(heroEls, {
        opacity: { from: 0, to: 1 },
        y: { from: 26, to: 0 },
        duration: 850,
        ease: 'outQuint',
        delay: stagger(120),
      });
    }

    // ── 2) Rise: einmaliges, sanftes Aufsteigen beim Viewport-Eintritt ────
    document.querySelectorAll<HTMLElement>('[data-motion="rise"]').forEach((el) => {
      const obs = onScroll({
        target: el,
        enter: 'bottom-=64 top',
        sync: 'play',
      });
      observers.push(obs);
      animate(el, {
        opacity: { from: 0, to: 1 },
        y: { from: 44, to: 0 },
        duration: 900,
        ease: 'outQuint',
        autoplay: obs,
      });
    });

    // ── 3) Slide: scroll-synchrones Hereinwischen (Problem/Lösung u. a.) ──
    const slide = (selector: string, fromX: number) => {
      document.querySelectorAll<HTMLElement>(selector).forEach((el) => {
        const obs = onScroll({
          target: el,
          enter: 'bottom top+=64',
          leave: 'center+=160 top',
          sync: 0.32, // weiches Nachziehen an die Scroll-Position
        });
        observers.push(obs);
        animate(el, {
          opacity: { from: 0.001, to: 1 },
          x: { from: fromX, to: 0 },
          ease: 'linear',
          autoplay: obs,
        });
      });
    };
    slide('[data-motion="slide-left"]', -44);
    slide('[data-motion="slide-right"]', 44);

    return () => {
      observers.forEach((obs) => obs.revert());
    };
  }, []);
}
